import { ApolloProvider } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useMount } from 'react-use';
import { waitFor } from 'xstate';

import { useApiClient } from '@eluve/api-client-provider';
import {
  BrowserSessionProvider,
  apolloClientFactory,
  defaultCache,
  sessionAccessTokenProvider,
} from '@eluve/apollo-client';
import { FCC, useToast } from '@eluve/components';
import { LoggerArgs, useExtendedLogger } from '@eluve/logger';
import { useSession, useTenantIdFromParams } from '@eluve/session-helpers';

import { appConfig } from '../../../../config';

const { sessionActor, tokenProvider } = sessionAccessTokenProvider({
  sessionProvider: new BrowserSessionProvider(),
});

const tenantScopedApolloClient = apolloClientFactory({
  uri: appConfig.VITE_APOLLO_CLIENT_URI,
  cacheInstance: defaultCache,
  accessTokenProvider: tokenProvider,
});

export const TenantGuard: FCC = ({ children }) => {
  const [machineInitialized, setMachineInitialized] = useState(false);
  const toast = useToast();

  const apiClient = useApiClient();

  const { pathname } = useLocation();
  const { appointmentId } = useParams<{ appointmentId?: string }>();

  const tenantParamsId = useTenantIdFromParams();

  const allowedTenants = useSession().allowedTenantIds;
  const tenantLoggerArgs: LoggerArgs = useMemo(
    () => ({ name: 'TenantGuard', metadata: { tenantId: tenantParamsId } }),
    [tenantParamsId],
  );

  const logger = useExtendedLogger(tenantLoggerArgs);

  useMount(() => {
    // We don't want to actually mount the ApolloClient until we know that the
    // session machine has acknolwedged the tenant change. This is only relevant
    // for users that have multiple tenants and are switching between them
    // in multiple tabs.
    waitFor(
      sessionActor,
      (state) => state.context.desiredTenantId === tenantParamsId,
    ).then(() => setMachineInitialized(true));
  });

  if (!tenantParamsId) {
    throw new Error('Expected tenant id in route params in TenantGuard');
  }

  useEffect(
    // Provide new values to the session management machine so that
    // it can reconcile to the desired tenant
    function updateTenantIdForSession() {
      sessionActor.send({
        type: 'CHANGE_TENANT',
        desiredTenantId: tenantParamsId,
      });

      sessionActor.send({ type: 'SUPPLY_LOGGER', logger });

      sessionActor.send({
        type: 'SUPPLY_RESET_LOGIC',
        reset: async () => {
          await apiClient.auth.createTenantSession({
            body: {
              tenantId: tenantParamsId,
            },
          });
        },
      });
    },
    [apiClient.auth, logger, tenantParamsId],
  );

  useEffect(function transitionToIdleSessionOnUnmount() {
    return () => {
      sessionActor.send({ type: 'SESSION_COMPONENT_UNMOUNTED' });
    };
  }, []);

  if (!allowedTenants.includes(tenantParamsId)) {
    const isAdminPath = pathname.includes('/admin');
    let redirectTo = isAdminPath ? '/admin' : '/';

    if (appointmentId) {
      redirectTo = `/admin/organizations?goToAppointmentId=${appointmentId}`;
    } else {
      toast.error('Error', {
        id: 'access-denied',
        description: 'Access denied',
      });
    }

    return <Navigate to={redirectTo} />;
  }

  // Wait for the session machine to be in a state where it has
  // acknowledged the correct tenant ID
  if (!machineInitialized) {
    return null;
  }

  return (
    <ApolloProvider client={tenantScopedApolloClient}>
      {children}
    </ApolloProvider>
  );
};
